/**
 * Universal fetch wrapper. Two modes:
 *
 * - SSR (server-side render): caller passes `csrf` (a token minted by
 *   server.js for this request) and `cookie` (forwarded from req.headers).
 *   The token is sent as the `csrf:` header. Server validates via HMAC.
 *
 * - Browser: caller passes neither. The wrapper reads the per-user
 *   `csrf_token` cookie set by GET /api/csrf and sends its value as the
 *   `csrf:` header on every /graphql or /api/* call. Server validates by
 *   double-submit (cookie === header) plus HMAC.
 *
 * Browser mode also auto-recovers from CSRF cookie expiry: if a /graphql
 * call gets rejected with 500 or 403, fetch a fresh /api/csrf token and
 * retry once. This means a tab left open across the cookie's max-age can
 * still mutate without the user having to reload.
 */

function getCsrfFromCookie() {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

// Browser only: SSR pre-populates window.App.ssrCache with responses to
// every /graphql and /api fetch the route action made server-side. During
// initial hydration the route action re-runs with the same arguments;
// matching keys are served from this cache so we skip the network entirely.
// Each entry is consumed once — subsequent client-side navigation makes
// real network requests.
function consumeSsrCache(url, options) {
  if (typeof window === 'undefined') return null;
  const cache = window.App && window.App.ssrCache;
  if (!cache) return null;
  const key = `${url}::${(options && options.body) || ''}`;
  if (!(key in cache)) return null;
  const text = cache[key];
  delete cache[key];
  return text;
}

function fakeJsonResponse(text) {
  return {
    ok: true,
    status: 200,
    json: () => Promise.resolve(JSON.parse(text)),
    text: () => Promise.resolve(text),
  };
}

function buildOptions(defaults, options, browserCsrf) {
  return {
    ...defaults,
    ...options,
    headers: {
      ...defaults.headers,
      ...(browserCsrf ? { csrf: browserCsrf } : null),
      ...(options && options.headers),
    },
  };
}

function createFetch(fetch, { baseUrl, cookie, csrf } = {}) {
  const defaults = {
    method: 'POST',
    mode: baseUrl ? 'cors' : 'same-origin',
    credentials: baseUrl ? 'include' : 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(cookie ? { Cookie: cookie } : null),
      ...(csrf ? { csrf } : null),
    },
  };

  const isSSR = Boolean(csrf);

  return async (url, options) => {
    if (!url.startsWith('/graphql') && !url.startsWith('/api')) {
      return fetch(url, options);
    }

    // Browser only: serve from SSR cache if present.
    if (!isSSR) {
      const cached = consumeSsrCache(url, options);
      if (cached !== null) return fakeJsonResponse(cached);
    }

    const fullUrl = `${baseUrl}${url}`;
    const browserCsrf = isSSR ? '' : getCsrfFromCookie();
    let res = await fetch(
      fullUrl,
      buildOptions(defaults, options, browserCsrf),
    );

    // Browser-only: if /graphql was rejected on auth, the CSRF cookie may
    // have expired. Re-issue and retry once before giving up.
    const looksLikeCsrfReject =
      !isSSR &&
      url.startsWith('/graphql') &&
      (res.status === 500 || res.status === 403);
    if (looksLikeCsrfReject) {
      try {
        await fetch(`${baseUrl}/api/csrf`, { credentials: 'same-origin' });
      } catch (e) {
        return res;
      }
      const fresh = getCsrfFromCookie();
      if (fresh) {
        res = await fetch(fullUrl, buildOptions(defaults, options, fresh));
      }
    }
    return res;
  };
}

export default createFetch;
